import type { SagaIterator } from 'redux-saga';
import { put, delay } from 'redux-saga/effects';
import { select as typedSelect, call as typedCall } from 'typed-redux-saga';
import { selectProfileOrganizationId } from 'flowhawk-core';

import parseProfile from '../Utils/profile';
import api from '../Services/Api';
import { apiFetch, apiPost } from './ApiSaga';
import { pushError } from '../Redux/ApplicationActions';
import {
  receiveOffers,
  receiveOrganizations,
  getOrganizationsFailed,
  receiveSurgeons,
  receiveFullOffer,
  saveChat,
  saveChatFailed,
} from '../Redux/ChatEditActions';
import { startLoading, finishLoading } from '../Redux/LoadingActions';
import { keys } from '../Utils/Object';
import type { ChatSaveError, CreateOffer } from '../Redux/ChatEditActions';
import { singleErrorMessage } from '../Utils/flattenError';
import type { Offer, Organization } from '../Utils/types';
import { receiveAvailableTags } from '../Redux/DonorActions';
import { convertToOrganization } from '../Utils/organizations';
import type { RootState } from '../Redux';
import type {
  ApiCaseOrgan,
  ApiCreateOfferCaseResponse,
  ApiGetOrganizationMemberResponse,
  ApiGetOrganizationOffersResponse,
  ApiOrganizationSelectResponse,
} from '../Utils/apiTypes';
import { ResourceErrorInterface } from 'tg-resources';

export function* createOfferSaga(action: CreateOffer): SagaIterator<void> {
  const { unosId, matchId, organType } = action;

  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const editData = yield* typedSelect((state: RootState) => state.chatEdit);

  if (!unosId || !matchId || !organType) {
    const errors: ChatSaveError = {};

    if (!unosId) {
      errors.unosId = 'UNOS ID is required when creating a case.';
    }
    if (!matchId) {
      errors.matchId = 'Match ID is required when creating a case.';
    }
    if (!organType) {
      errors.organType = 'Organ type is required when creating a case.';
    }
    if (!editData.name) {
      errors.name = 'Room name is required.';
    }

    yield put(saveChatFailed(errors));

    return;
  }

  const orgId = yield* typedSelect((state: RootState) => selectProfileOrganizationId(state.auth));

  const donorObject = {
    organization_id: orgId,
    identification: {
      unos_id: unosId,
      match_id: matchId,
      organ_type: organType,
    },
    demographics: {},
    admission: {},
    history: {},
    transplant: {},
    medications: {},
    labs: {},
    observations: {},
    misc: {},
  };

  const { result, error } = yield* typedCall(
    apiPost<ApiCreateOfferCaseResponse>,
    api.txp.createOffer,
    undefined,
    donorObject,
  );

  if (error || !result) {
    const errors: ChatSaveError = {};
    if (error instanceof ResourceErrorInterface) {
      if (error.isValidationError) {
        errors.nonFieldError = singleErrorMessage(error);
        yield put(saveChatFailed(errors));
      } else if (error.isInvalidResponseCode) {
        errors.nonFieldError = 'Donor record has already been created.';
        yield put(saveChatFailed(errors));
      } else if (error.isNetworkError) {
        errors.nonFieldError = 'Creating room failed, are you online?';
        yield put(saveChatFailed(errors));
      } else {
        errors.nonFieldError = 'Creating room failed, try again later.';
        yield put(saveChatFailed(errors));
      }
    } else {
      errors.nonFieldError = 'Creating room failed, try again later.';
      yield put(saveChatFailed(errors));
    }
  } else {
    const { id, name, description, message, mentionedPendingUsers, attachments, managed } = editData;

    const pendingUsers = yield* typedSelect((state: RootState) => state.chatEdit.pendingUsers);

    yield put(
      saveChat(
        id,
        name,
        pendingUsers,
        managed,
        message,
        mentionedPendingUsers,
        description,
        attachments,
        result.donor_id,
        result.organ_id,
        0,
        undefined,
        'Case',
      ),
    );
  }
}

export function* loadOffersSaga(): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('offers'));

  const { result, error } = yield* typedCall(apiFetch<ApiGetOrganizationOffersResponse>, api.txp.getOffers);

  if (error || !result) {
    yield put(pushError('Failed to get offers', error));
    yield put(finishLoading('offers'));
  } else {
    const { donors } = result;

    const offers: Offer[] = [];

    const donorKeys = keys(donors);

    for (let i = 0; i < donorKeys.length; i += 1) {
      const donorKey = parseInt(donorKeys[i], 10);
      const donorInfo = donors[donorKey];
      for (let j = 0; j < donorInfo.organs.length; j += 1) {
        const organInfo = donorInfo.organs[j];

        const offer: Offer = {
          donorId: parseInt(donorKeys[i], 10),
          unosId: donorInfo.unos_id,
          organId: organInfo.organ_id,
          matchId: organInfo.match_id,
          organType: organInfo.organ_type,
        };

        offers.push(offer);
      }
    }

    offers.sort((a, b) => (a.donorId < b.donorId ? 1 : -1));

    yield put(receiveOffers(offers));

    yield put(finishLoading('offers'));
  }
}

export function* loadOrganizationsSaga(): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('organizations'));

  const { result, error } = yield* typedCall(apiFetch<ApiOrganizationSelectResponse>, api.txp.getOrganizations);

  if (error || !result) {
    yield put(pushError('Failed to get organizations', error));
    yield put(getOrganizationsFailed());
    yield put(finishLoading('organizations'));
  } else {
    const organizations: Organization[] = [];
    const orgArray = result.organizations;

    const orgId = yield* typedSelect((state: RootState) => selectProfileOrganizationId(state.auth));

    for (let i = 0; i < orgArray.length; i += 1) {
      const singleOrg = convertToOrganization(orgArray[i]);
      organizations.push(singleOrg);

      if (singleOrg.id === orgId) {
        yield put(receiveAvailableTags(singleOrg.availableTags));
      }
    }

    organizations.sort(compare);

    yield put(receiveOrganizations(organizations));
    yield put(finishLoading('organizations'));
  }
}

export function* loadSurgeonsSaga(): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  yield put(startLoading('surgeons'));

  const organizationId = yield* typedSelect((state: RootState) => state.chatEdit.organization.id);

  const { result, error } = yield* typedCall(
    apiFetch<ApiGetOrganizationMemberResponse>,
    api.txp.getOrganizationMembers,
    {
      orgId: organizationId,
    },
    {
      organizational_role: 'Surgeon',
    },
  );

  if (error || !result) {
    yield put(pushError('Failed to get surgeons', error));
    yield put(finishLoading('surgeons'));
  } else {
    const surgeons = [];
    const surgeonsArray = result.organization_members;

    for (let i = 0; i < surgeonsArray.length; i += 1) {
      surgeons.push(parseProfile(surgeonsArray[i].user_information));
    }

    yield put(receiveSurgeons(surgeons));

    yield put(finishLoading('surgeons'));
  }
}

export function* getOfferSaga(): SagaIterator<void> {
  // Debounce the task: https://github.com/redux-saga/redux-saga/blob/master/docs/recipes/README.md#debouncing
  yield delay(50);

  const donorId = yield* typedSelect((state: RootState) => state.chatEdit.offer.donorId);
  const organId = yield* typedSelect((state: RootState) => state.chatEdit.offer.organId);

  const { result, error } = yield* typedCall(apiFetch<ApiCaseOrgan>, api.txp.getOffer, {
    donorId,
    organId,
  });

  if (error || !result) {
    yield put(pushError('Failed to get offer', error));
  } else {
    yield put(receiveFullOffer(result));
  }
}

// Sort array of organization objects by the organization name (A-Z)
// https://www.sitepoint.com/sort-an-array-of-objects-in-javascript/
function compare(a: Organization, b: Organization) {
  const orgA = a.name.toUpperCase();
  const orgB = b.name.toUpperCase();

  let comparison = 0;

  if (orgA > orgB) {
    comparison = 1;
  } else if (orgA < orgB) {
    comparison = -1;
  }

  return comparison;
}
